import { Info } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { metricDefinition, type MetricKey } from '@/lib/financeGlossary'

interface MetricLabelProps {
  metric: MetricKey
  /**
   * Optional override for the visible label — handy when a UI surface
   * uses a slightly different shorthand ("Sales" instead of "Net
   * Sales") but should still pull the canonical description from the
   * glossary.
   */
  label?: string
  /** Tooltip placement. */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Match the surrounding text style. */
  className?: string
  /** Hide the (i) icon when the label itself is the obvious affordance. */
  hideIcon?: boolean
}

/**
 * Renders a metric label with a discoverable tooltip explaining what
 * the number means. Wraps every place we surface money so users know
 * exactly what each number includes/excludes.
 */
export function MetricLabel({
  metric,
  label,
  side = 'top',
  className,
  hideIcon = false,
}: MetricLabelProps) {
  const def = metricDefinition(metric)
  const visible = label ?? def?.label ?? metric

  if (!def) {
    return <span className={className}>{visible}</span>
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center gap-1 cursor-help underline-offset-2 decoration-dotted hover:underline',
              className,
            )}
          >
            <span>{visible}</span>
            {!hideIcon ? (
              <Info className="h-3 w-3 text-muted-foreground/70" aria-hidden />
            ) : null}
          </span>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs text-left">
          <div className="space-y-1">
            <div className="text-sm font-semibold leading-tight text-popover-foreground">
              {def.label}
            </div>
            <p className="text-xs leading-relaxed text-popover-foreground/90">
              {def.summary}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
